import React from 'react';

import {
  minValue,
  required,
  SelectInput,
  NumberInput,
} from 'react-admin';

import {
  Grid,
} from '@mui/material';

import { useWatch } from 'react-hook-form';

import PriorityParameters from './PriorityParameters';
import ThresholdModeSelector from './ThresholdModeSelector';
import KycFormDataMaxAgeInput from './KycFormDataMaxAgeInput';

import Label from '../../../customFields/Label';

import { timePeriodChoices } from '../../../constants/timePeriods';
import thresholdModes from '../../../constants/thresholdModes';
import TransactionTypeConfiguration from './TransactionTypeConfiguration';
import transactionMonitoringModes, {
  transactionMonitoringModesChoices,
} from '../../../constants/transactionMonitoringMode';
import anomalyDeviationDirections, {
  anomalyDeviationDirectionChoices,
} from '../../../constants/anomalyDeviationDirections';
import { DefaultThresholdInput, KycFormThresholdInput } from './ThresholdInput';

const transactionDirectionChoices = [
  { id: 'incoming', name: 'Incoming' },
  { id: 'outgoing', name: 'Outgoing' },
  { id: 'processing', name: 'Processing' },
  { id: 'total', name: 'Total' },
];

const TransactionMonitoringTypicalAmount = () => {
  const thresholdMode = useWatch({ name: 'ruleParameters.thresholdMode' });

  const showDefault = thresholdMode === thresholdModes.DEFAULT_ONLY
    || thresholdMode === thresholdModes.BOTH;
  const showForm = thresholdMode === thresholdModes.KYC_ONLY
    || thresholdMode === thresholdModes.BOTH;
  const showPriority = thresholdMode === thresholdModes.BOTH;

  return (
    <Grid size={12}>
      <Grid container spacing={1}>
        <Grid size={12}>
          <Grid container spacing={1}>
            <Grid size={12}>
              <TransactionTypeConfiguration />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <SelectInput
                source="ruleParameters.transactionMonitoringMode"
                choices={transactionMonitoringModesChoices}
                fullWidth
                label="Transaction monitoring mode"
                validate={[required()]}
                defaultValue={transactionMonitoringModes.ALL_RELATED}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <SelectInput
                source="ruleParameters.transactionDirection"
                choices={transactionDirectionChoices}
                fullWidth
                label="Transaction direction"
                validate={[required()]}
                defaultValue="total"
              />
            </Grid>
            <ThresholdModeSelector />
            {showDefault && (
              <Grid size={12}>
                <DefaultThresholdInput />
              </Grid>
            )}
            {showForm && (
              <Grid size={12}>
                <KycFormThresholdInput
                  displayText="Deviation in %"
                  allowCommon
                />
              </Grid>
            )}
            {showForm && <KycFormDataMaxAgeInput />}
            <Grid
              size={12}
            >
              <SelectInput
                choices={anomalyDeviationDirectionChoices}
                defaultValue={anomalyDeviationDirections.BOTH}
                source="ruleParameters.deviationDirection"
                label="Deviation direction"
                fullWidth
              />
            </Grid>
            <Grid size={12}>
              <Label
                variant="label"
                title="Period"
                info="The period used to evaluate the historical baseline for typical amount."
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <NumberInput
                source="ruleParameters.period.amount"
                fullWidth
                label="Length"
                validate={[required(), minValue(1)]}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <SelectInput
                source="ruleParameters.period.unit"
                choices={timePeriodChoices}
                fullWidth
                label="Period"
                validate={[required()]}
              />
            </Grid>

            <Grid size={12}>
              <Label
                variant="label"
                title="Minimum number of observations"
                info="Minimum number of historical observations required before evaluating this rule."
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <NumberInput
                source="ruleParameters.minimumNumberOfObservations"
                fullWidth
                validate={[required(), minValue(1)]}
              />
            </Grid>
            <Grid size={12}>
              <Label
                variant="label"
                title="Minimum usage time"
                info="The entity must have existed for at least this period before rule evaluation."
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <NumberInput
                source="ruleParameters.minimumUsageTime.amount"
                fullWidth
                label="Length"
                validate={[required(), minValue(1)]}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6,
              }}
            >
              <SelectInput
                source="ruleParameters.minimumUsageTime.unit"
                choices={timePeriodChoices}
                fullWidth
                label="Period"
                validate={[required()]}
              />
            </Grid>
          </Grid>
        </Grid>
        {showPriority && <PriorityParameters />}
      </Grid>
    </Grid>
  );
};

export default TransactionMonitoringTypicalAmount;
